import { OVERLAY, BUTTON_SUBMISSION, STASH, ERROR } from "../constants";

export const manageUsers = () => {
  const trackEvent = (event) => {
    digitalData.event = digitalData.event || [];
    digitalData.event.push(event);
  };

  const manageUsersEvents = {
    drawerOverlay: ({ output }) => {
      const step = output?.step;
      const DDOEvent = {
        category: {
          primaryCategory: OVERLAY,
        },
        eventInfo: {
          eventName: `user management: ${step}`,
        },
      };
      trackEvent(DDOEvent);
    },
    selectedCreditCards: ({ output }) => {
      // Format card types and counts into a string (e.g., "MA>5,DISC>3,VISA>2")
      const cardEntries = Object.entries(output || {});
      const cardDetails =
        cardEntries.length > 0
          ? cardEntries.map(([cardType, count]) => `${cardType}>${count}`).join(",")
          : "none shared";

      const DDOEvent = {
        category: {
          primaryCategory: BUTTON_SUBMISSION,
        },
        eventInfo: {
          eventName: `my account settings`,
        },
        myAccount: {
          settings: `user management: credit cards overlay: apply: ${cardDetails}`,
        },
      };
      trackEvent(DDOEvent);
    },
    editSelectedPaymentPermissions: ({ output }) => {
      const role = output?.role?.toLowerCase();
      const ccPermissions = output?.ccPermissions;
      const pTCPermissions = output?.pTCPermissions;

      const formatPermissions = (cc, ptc) => {
        const parts = [];
        if (cc !== undefined && cc !== null && cc !== 0) parts.push(`cc>${cc}`);
        if (ptc !== undefined && ptc !== null && ptc !== 0) parts.push(`ptc>${ptc}`);
        return parts.length > 0 ? parts.join(",") : "";
      };

      const permissionsString = formatPermissions(ccPermissions, pTCPermissions);

      const DDOEvent = {
        category: {
          primaryCategory: BUTTON_SUBMISSION,
        },
        eventInfo: {
          eventName: `my account settings`,
        },
        myAccount: {
          settings: `user management: edit user: type>${role}${permissionsString ? `: ${permissionsString}` : ""}`,
        },
      };
      trackEvent(DDOEvent);
    },
    cancelClick: ({ output }) => {
      const step = output?.step;
      const DDOEvent = {
        category: {
          primaryCategory: BUTTON_SUBMISSION,
        },
        eventInfo: {
          eventName: `my account settings`,
        },
        myAccount: {
          settings: `user management flow: ${step} overlay: cancel`,
        },
      };
      trackEvent(DDOEvent);
    },
    addUserSuccess: ({ output }) => {
      const role = output?.role?.toLowerCase();
      const ccPermissions = output?.ccPermissions;
      const pTCPermissions = output?.pTCPermissions;

      const formatPermissions = (cc, ptc) => {
        const hasCC = cc !== undefined && cc !== null && cc !== 0;
        const hasPTC = ptc !== undefined && ptc !== null && ptc !== 0;
        const parts = [];
        if (hasCC) parts.push(`cc>${cc}`);
        if (hasPTC) parts.push(`ptc>${ptc}`);
        return parts.length > 0 ? parts.join(",") : "";
      };

      const permissionsString = formatPermissions(ccPermissions, pTCPermissions);

      const DDOEvent = {
        category: {
          primaryCategory: BUTTON_SUBMISSION,
        },
        eventInfo: {
          eventName: `my account settings`,
        },
        myAccount: {
          settings: permissionsString
            ? `user management: add user: ${role}: yes: ${permissionsString}`
            : `user management: add user: ${role}: no`,
        },
      };
      trackEvent(DDOEvent);
    },
    linkClick: ({ output }) => {
      const linkName = output?.linkName;
      const DDOEvent = {
        category: {
          primaryCategory: BUTTON_SUBMISSION,
        },
        eventInfo: {
          eventName: `my account settings`,
        },
        myAccount: {
          settings: `user management: ${linkName} link`,
        },
      };
      trackEvent(DDOEvent);
    },
    // setBulkAddEventAnalytics(events)
    bulkAddEvent: ({ output }) => {
      const events = output?.events;
      const DDOEvent = {
        category: {
          primaryCategory: BUTTON_SUBMISSION,
        },
        eventInfo: {
          eventName: `my account settings`,
        },
        myAccount: {
          settings: `user management: bulk: ${events}`,
        },
      };
      trackEvent(DDOEvent);
    },
    // setSearchSortResultsAnalytics
    searchSortResults: ({ output }) => {
      const action = output?.action;
      const count = output?.count;
      const sortBy = output?.sortBy;
      const order = output?.order;
      const searchType = output?.searchType;
      const searchValue = output?.searchValue;

      const DDOEvent = {
        category: {
          primaryCategory: BUTTON_SUBMISSION,
        },
        eventInfo: {
          eventName: `my account settings`,
        },
        myAccount: {
          settings: `user management: ${action}: ${count}`,
          searchTerm: `${sortBy}:${order}`,
          searchBy: `${searchType}:${searchValue}`,
        },
      };
      trackEvent(DDOEvent);
    },
    // pushUserManagementEvent(action) - covers setRemoveUserSuccessAnalytics,
    // setBulkRemoveUsersSuccessAnalytics, setResendUserInvitesSuccessAnalytics,
    // setHeaderLinkAnalytics, setMultiSelectOperationLinkAnalytics,
    // setPurchaserPermissionsToggleAnalytics, setCardLinkAnalytics,
    // setAddUserSuccessAnalytics, setEditUserSuccessAnalytics, setMeatBallsAnalytics
    // - each caller passes its own fully-formatted "action" string.
    userManagementEvent: ({ output }) => {
      const action = output?.action;
      const DDOEvent = {
        category: {
          primaryCategory: BUTTON_SUBMISSION,
        },
        eventInfo: {
          eventName: `my account settings`,
        },
        myAccount: {
          settings: `user management: ${action}`,
        },
      };
      trackEvent(DDOEvent);
    },
    // setMeatBallsAnalytics('assign pro allowance') - stash variant (fixed string)
    assignProAllowance: () => {
      const DDOEvent = {
        category: {
          primaryCategory: STASH,
        },
        eventInfo: {
          eventName: `my account settings`,
        },
        eventMode: STASH,
        myAccount: {
          settings: `user management: meatball: assign pro allowance`,
        },
      };
      trackEvent(DDOEvent);
    },
    // setAddUserOverlayAnalytics (src/utils/add-manage-user-analytics.ts) - not currently called in source
    addUserOverlay: () => {
      const DDOEvent = {
        category: {
          primaryCategory: OVERLAY,
        },
        eventInfo: {
          eventName: `user management: add user`,
        },
      };
      trackEvent(DDOEvent);
    },
    // setEditUserOverlayAnalytics (src/utils/add-manage-user-analytics.ts) - not currently called in source
    editUserOverlay: () => {
      const DDOEvent = {
        category: {
          primaryCategory: OVERLAY,
        },
        eventInfo: {
          eventName: `user management: edit user`,
        },
      };
      trackEvent(DDOEvent);
    },
    // setResendUserInvitesOverlayAnalytics (multi-resend-invite-modal.tsx)
    resendInvitesOverlay: () => {
      const DDOEvent = {
        category: {
          primaryCategory: OVERLAY,
        },
        eventInfo: {
          eventName: `user management: resend user invites`,
        },
      };
      trackEvent(DDOEvent);
    },
    // setRemoveUserOverlayAnalytics (delete-user-modal.tsx)
    removeUserOverlay: () => {
      const DDOEvent = {
        category: {
          primaryCategory: OVERLAY,
        },
        eventInfo: {
          eventName: `user management: remove user`,
        },
      };
      trackEvent(DDOEvent);
    },
    // setOverlayEvent('user management bulk remove user') (multi-delete-user-modal.tsx)
    bulkRemoveOverlay: () => {
      const DDOEvent = {
        category: {
          primaryCategory: OVERLAY,
        },
        eventInfo: {
          eventName: `user management bulk remove user`,
        },
      };
      trackEvent(DDOEvent);
    },
    // setOverlayEvent("invalid users can't be imported") (no-valid-users-modal.tsx)
    invalidUsersOverlay: () => {
      const DDOEvent = {
        category: {
          primaryCategory: OVERLAY,
        },
        eventInfo: {
          eventName: `invalid users can't be imported`,
        },
      };
      trackEvent(DDOEvent);
    },
    // setOverlayEvent('cancel entire user import') (cancel-import.tsx)
    cancelImportOverlay: () => {
      const DDOEvent = {
        category: {
          primaryCategory: OVERLAY,
        },
        eventInfo: {
          eventName: `cancel entire user import`,
        },
      };
      trackEvent(DDOEvent);
    },
    // setOverlayEvent('remove invalid users and continue import') (remove-invalid-users.tsx)
    removeInvalidUsersOverlay: () => {
      const DDOEvent = {
        category: {
          primaryCategory: OVERLAY,
        },
        eventInfo: {
          eventName: `remove invalid users and continue import`,
        },
      };
      trackEvent(DDOEvent);
    },
    // setUserPostPageErrorAnalytics(pageSection, displayType, errorCode, errorMessage) -> pushErrorEvent
    // (src/utils/add-manage-user-analytics.ts) - bulk add upload/submit and multi-user-action error paths
    userManagementErrorEvent: ({ output }) => {
      const pageSection = output?.pageSection || "bulk add";
      const displayType = output?.displayType || "inline";
      const code = output?.code || "";
      const message = output?.message || "";

      const DDOEvent = {
        category: {
          primaryCategory: ERROR,
        },
        eventInfo: {
          eventName: `post page error`,
          error: [{
            type: "error",
            displayType,
            pageSection,
            code,
            message,
          }],
        },
      };
      trackEvent(DDOEvent);
    },
  };

  window.LIFE_CYCLE_EVENT_BUS.on("manage-users.drawer-overlay", manageUsersEvents.drawerOverlay);
  window.LIFE_CYCLE_EVENT_BUS.on(
    "manage-users.selected-credit-cards",
    manageUsersEvents.selectedCreditCards,
  );
  window.LIFE_CYCLE_EVENT_BUS.on(
    "manage-users.edit-selected-payment-permissions",
    manageUsersEvents.editSelectedPaymentPermissions,
  );
  window.LIFE_CYCLE_EVENT_BUS.on("manage-users.cancel-click", manageUsersEvents.cancelClick);
  window.LIFE_CYCLE_EVENT_BUS.on("manage-users.add-user-success", manageUsersEvents.addUserSuccess);
  window.LIFE_CYCLE_EVENT_BUS.on("manage-users.link-click", manageUsersEvents.linkClick);
  window.LIFE_CYCLE_EVENT_BUS.on("manage-users.bulk-add-event", manageUsersEvents.bulkAddEvent);
  window.LIFE_CYCLE_EVENT_BUS.on(
    "manage-users.search-sort-results",
    manageUsersEvents.searchSortResults,
  );
  window.LIFE_CYCLE_EVENT_BUS.on(
    "manage-users.user-management-event",
    manageUsersEvents.userManagementEvent,
  );
  window.LIFE_CYCLE_EVENT_BUS.on(
    "manage-users.assign-pro-allowance",
    manageUsersEvents.assignProAllowance,
  );
  window.LIFE_CYCLE_EVENT_BUS.on("manage-users.add-user-overlay", manageUsersEvents.addUserOverlay);
  window.LIFE_CYCLE_EVENT_BUS.on("manage-users.edit-user-overlay", manageUsersEvents.editUserOverlay);
  window.LIFE_CYCLE_EVENT_BUS.on(
    "manage-users.resend-invites-overlay",
    manageUsersEvents.resendInvitesOverlay,
  );
  window.LIFE_CYCLE_EVENT_BUS.on("manage-users.remove-user-overlay", manageUsersEvents.removeUserOverlay);
  window.LIFE_CYCLE_EVENT_BUS.on("manage-users.bulk-remove-overlay", manageUsersEvents.bulkRemoveOverlay);
  window.LIFE_CYCLE_EVENT_BUS.on(
    "manage-users.invalid-users-overlay",
    manageUsersEvents.invalidUsersOverlay,
  );
  window.LIFE_CYCLE_EVENT_BUS.on(
    "manage-users.cancel-import-overlay",
    manageUsersEvents.cancelImportOverlay,
  );
  window.LIFE_CYCLE_EVENT_BUS.on(
    "manage-users.remove-invalid-users-overlay",
    manageUsersEvents.removeInvalidUsersOverlay,
  );
  window.LIFE_CYCLE_EVENT_BUS.on(
    "manage-users.error-event",
    manageUsersEvents.userManagementErrorEvent,
  );
};
